<script lang="ts" setup>
import { computed, type ComputedRef } from 'vue'
import { t } from '@/locales/i18n'
import type { Filter, TUiTypeValues } from '@/types/api/reports'
import GwSelectNew from '@/components/lib/GwSelectNew/GwSelectNew.vue'
import PickerComponent from '@/components/hhv3/Analyzer/Filters/PickerComponent.vue'
import SelectedItemsComponent from '@/components/hhv3/Analyzer/Filters/SelectedItemsComponent.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import GwUiType from '@/components/lib/GwTable/GwUiType.vue'
import * as R from 'remeda'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import {
  type ActionSequence,
  displaySelectedSequence,
  getSequenceLength,
} from '@/components/lib/GwTable/ActionSequence/actionSequenceUtils'

export interface AnalyzerFilterProps {
  filters: Filter[]
  tstPrefix?: string
}

const props = defineProps<AnalyzerFilterProps>()

export type FilterEmits = {
  'update:filter': [item: Filter]
  'remove:filter': [item: Filter]
}

const emit = defineEmits<FilterEmits>()

const sortedFilters = computed(() =>
  R.sortBy.strict(props.filters, ({ ordering_position }) => ordering_position),
)

const activeFilters: ComputedRef<Filter[]> = computed(() =>
  sortedFilters.value.filter(f => f.is_active),
)
</script>
<template>
  <div
    class="d-flex"
    style="gap: var(--app-gap-1)"
  >
    <template
      v-for="item in activeFilters"
      :key="item.filter.field_name"
    >
      <GwBox
        v-if="item.filter.ui_value_type === 'readonly_filename'"
        class="no-shrink f-center"
        variant="box_select"
        style="padding: 0 var(--app-pad-3)"
        :data-tst="
          tstPrefix
            ? `${tstPrefix}_filter_${item.filter.field_name}`
            : `filter_${item.filter.field_name}`
        "
      >
        <GwText
          variant="text_grey_medium_500"
          class="text-nowrap"
          v-html="t(`hhv3.table_head.${item.filter.field_name}`)"
        />
        <GwBox
          v-for="(value, idx) in [item.metadata?.display_value || item.value].flat()"
          :key="idx"
          variant="box_select_pill"
          class="no-events"
        >
          <GwUiType
            :value="value as TUiTypeValues['readonly_filename']"
            :uiType="item.filter.ui_value_type"
          />
        </GwBox>
      </GwBox>

      <GwSelectNew
        v-else
        closeOnScroll
        class="cursor-pointer"
        :title="t(`hhv3.table_head.${item.filter.field_name}`)"
        :data-tst="
          tstPrefix
            ? `${tstPrefix}_filter_${item.filter.field_name}`
            : `filter_${item.filter.field_name}`
        "
      >
        <template #opener_content>
          <GwBtn
            v-if="item.filter.filter_ui_type === 'ACTIONS_SEQUENCE'"
            v-show="getSequenceLength((item.value || [[]]) as ActionSequence)"
            variant="btn_text_primary_primary"
            active
            class="text-center"
            v-html="displaySelectedSequence((item.value || [[]]) as ActionSequence)"
          />
          <div
            v-else-if="item.value"
            @click.stop
          >
            <SelectedItemsComponent
              :item
              @update:filter="item => emit('update:filter', item)"
            />
          </div>
        </template>
        <template #content="{ methods }">
          <div @click.stop>
            <PickerComponent
              :item
              btnGroupBoxVariant="box_column"
              btnGroupDirection="vertical"
              @update:filter="item => emit('update:filter', item)"
              @remove:filter="
                item => {
                  emit('remove:filter', item)
                  methods.close()
                }
              "
            />
          </div>
        </template>
      </GwSelectNew>
    </template>
  </div>
</template>
